import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { FormControl, Typography } from '@material-ui/core';
import { getModalSwitch, getLoaderBtn } from '@app/store/actions';
import { useTypedTranslation } from '@app/hooks';
import {
    ModalActionsComponent,
    PasslySelect,
    PasslyDivider,
    PasslyCheckbox,
    PasslyTextField
} from '@app/components';
import { FormMode } from '@app/helpers/enums';
import { provisioningStates, provisioned, created, defaultOrgId, onPressEnterModal, genericErrorString } from '@app/helpers';
import { IProvisioningPolicy, IProvisioningPolicyElement } from '@app/models';
import { provisioningPoliciesService } from '@app/services';

export interface IReceivedData {
    formMode: FormMode;
    policyId?: string;
    originalPolicyName?: string;
    handleRefresh: Function;
}

export function AddEditProvisioningPolicyDialog(props: IReceivedData) {
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    const [policyName, setPolicyName] = useState('');
    const [provisioningSelected, setProvisioningSelected] = useState(created);
    const [enforce2fa, setEnforce2fa] = useState(true);
    const [onboardingEmail, setOnboardingEmail] = useState(false);
    const [agentMerge, setAgentMerge] = useState(false);
    const [disablePolicyNameField, setDisablePolicyNameField] = useState(false);
    const [provisioningPolicy, setProvisioningPolicy] = useState({} as IProvisioningPolicy);
    const [configFinished, setConfigFinished] = useState(false);
    const [displayNameFinished, setDisplayNameFinished] = useState(false);

    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        if (props.formMode === FormMode.Edit) {
            if (Object.keys(provisioningPolicy).length === 0 && props.policyId) {
                provisioningPoliciesService.getPolicyById(props.policyId, handleError, handleSuccessGetData);
            }
        }
    }, []);

    useEffect(() => {
        if (configFinished && displayNameFinished) {
            dispatch(getModalSwitch(false));
            dispatch(getLoaderBtn(false));

            if (props.formMode === FormMode.Add) {
                props.handleRefresh(true);
            }
            else if (policyName !== props.originalPolicyName) {
                props.handleRefresh(true);
            }
            else {
                props.handleRefresh(false);
            }
        }
    }, [configFinished, displayNameFinished]);

    const handleSuccessGetData = (results: any) => {
        setProvisioningPolicy(results.data);

        const returnedPolicy: IProvisioningPolicy = results.data;

        setPolicyName(returnedPolicy.DisplayName);

        if (returnedPolicy.IsDefault) {
            setDisablePolicyNameField(true);
        }

        returnedPolicy.Elements.forEach(function (currentElement: IProvisioningPolicyElement) {
            const config: any = JSON.parse(currentElement.Configuration);

            switch (currentElement.Type.toUpperCase()) {
                case 'INITIALSTATE':
                    setProvisioningSelected(config.state);
                    break;
                case 'ENFORCEMFA':
                    setEnforce2fa(config.value);
                    break;
                case 'MERGE':
                    setAgentMerge(config.value);
                    break;
                case 'SENDONBOARDING':
                    setOnboardingEmail(config.value);
                    break;
                default:
                    break;
            }
        });
    };

    const onFormChange = (e: any) => {
        const { name, value } = e.target;

        switch (name) {
            case 'policyName':
                setPolicyName(value);
                break;
            case 'provisioningSelected':
                setProvisioningSelected(value);
                changeFormBasedOnProvisioningState(value);
                break;
            default:
                break;
        }
    };

    const onCheckboxChange = (checked: boolean, e: any) => {
        const { name } = e.target;

        switch (name) {
            case 'enforce2fa':
                setEnforce2fa(checked);
                break;
            case 'onboardingEmail':
                setOnboardingEmail(checked);
                break;
            case 'agentMerge':
                setAgentMerge(checked);
                break;
            default:
                break;
        }
    };

    const changeFormBasedOnProvisioningState = (provChosen: string) => {
        if (provChosen !== provisioned) {
            setOnboardingEmail(false);
        }
    };

    const handleSubmitBtn = () => {
        dispatch(getLoaderBtn(true));

        const valid: boolean = validateForm();

        if (!valid) {
            dispatch(getLoaderBtn(false));
            return;
        }

        let addedForm: IProvisioningPolicy = {} as IProvisioningPolicy;
        addedForm['@odata.type'] = 'Kaseya.AuthAnvil.Models.ProvisioningPolicy';
        addedForm.DisplayName = policyName;
        addedForm.IsDefault = false;
        addedForm.OrganizationId = defaultOrgId;
        addedForm.Elements = [];

        let element: IProvisioningPolicyElement = {} as IProvisioningPolicyElement;
        element['@odata.type'] = 'Kaseya.AuthAnvil.Models.ProvisioningPolicyElement';
        element.Configuration = `{"state":"${provisioningSelected}","condition":"onCreate"}`;
        element.Enabled = true;
        element.Type = 'InitialState';

        addedForm.Elements.push(element);

        element = {} as IProvisioningPolicyElement;
        element['@odata.type'] = 'Kaseya.AuthAnvil.Models.ProvisioningPolicyElement';
        element.Configuration = `{"value":${enforce2fa},"attribute":"mfa","action":"add","condition":"onCreate"}`;
        element.Enabled = true;
        element.Type = 'EnforceMFA';

        addedForm.Elements.push(element);

        element = {} as IProvisioningPolicyElement;
        element['@odata.type'] = 'Kaseya.AuthAnvil.Models.ProvisioningPolicyElement';
        element.Configuration = `{"value":${onboardingEmail},"attribute":"notify","action":"add","condition":"onCreate"}`;
        element.Enabled = true;
        element.Type = 'SendOnboarding';

        addedForm.Elements.push(element);

        element = {} as IProvisioningPolicyElement;
        element['@odata.type'] = 'Kaseya.AuthAnvil.Models.ProvisioningPolicyElement';
        element.Configuration = `{"value":${agentMerge},"attribute":"remap","action":"add","condition":"onCreate"}`;
        element.Enabled = true;
        element.Type = 'Merge';

        addedForm.Elements.push(element);

        if (props.formMode === FormMode.Add) {
            provisioningPoliciesService.addNewPolicy(addedForm, handleError, handleSuccessAll);
        }
        else if (props.formMode === FormMode.Edit) {
            if (props.policyId) {
                // there are 2 separate calls for update. 1 call to update the checkboxes and the 2nd call to update the policy name.

                provisioningPoliciesService.updatePolicyElements(props.policyId, addedForm.Elements, handleError, handleSuccessPolicyElements);

                if (policyName !== props.originalPolicyName) {
                    (delete (addedForm as any).Elements);
                    (delete (addedForm as any).OrganizationId);

                    provisioningPoliciesService.updatePolicy(props.policyId, addedForm, handleError, handleSuccessDisplayName);
                }
                else {
                    handleSuccessDisplayName();
                }
            }
        }
    };

    const validateForm = () => {
        if (policyName === '') {
            setErrorMessage(translation('POLICY_NAME') + ' ' + translation('IS_REQUIRED'));
            return false;
        }

        return true;
    };

    const handleSuccessPolicyElements = () => {
        setConfigFinished(true);
    };

    const handleSuccessDisplayName = () => {
        setDisplayNameFinished(true);
    };

    const handleSuccessAll = () => {
        handleSuccessPolicyElements();
        handleSuccessDisplayName();
    };

    const handleError = (error: any) => {
        dispatch(getLoaderBtn(false));
        setErrorMessage(genericErrorString(error));
    };

    const handleCancel = () => {
        dispatch(getModalSwitch(false));
        dispatch(getLoaderBtn(false));
    };

    const okButtonText = () => {
        if (props.formMode === FormMode.Add) {
            return translation('ADD_POLICY');
        }
        else {
            return translation('SAVE_CHANGES');
        }
    };

    return (
        <>
            <form className='modal__form' method='post' tabIndex={0} onKeyPress={onPressEnterModal(handleSubmitBtn)}>
                <FormControl className='modal__form--password' required>
                    <PasslyTextField
                        fullWidth
                        id='policyName-input'
                        label={'POLICY_NAME'}
                        name='policyName'
                        value={policyName}
                        onChange={onFormChange}
                        disabled={disablePolicyNameField}
                    />
                </FormControl>

                <div className='modal__body--row'>
                    <PasslyDivider />
                    <h4>{translation('POLICY_PROVISIONING_INITIAL_STATE')}</h4>
                    <Typography variant='body1'>{translation('POLICY_PROVISIONING_INITIAL_STATE_DESCRIPTION')}</Typography>
                    <div className='modal__body--inner_row'>
                        <FormControl>
                            <PasslySelect
                                name='provisioningSelected'
                                value={provisioningSelected}
                                onChange={onFormChange}
                                id='initialState-input'
                                label={'POLICY_PROVISIONING_INITIAL_STATE_HEADER'}
                                options={provisioningStates}
                                required
                            />
                        </FormControl>
                    </div>
                </div>

                <div className='modal__body--row'>
                    <PasslyDivider />
                    <h4>{translation('POLICY_PROVISIONING_ENFORCE_MFA')}</h4>
                    <Typography variant='body1'>{translation('POLICY_PROVISIONING_ENFORCE_MFA_DESCRIPTION')}</Typography>
                    <div className='modal__body--inner_row'>
                        <PasslyCheckbox
                            name='enforce2fa'
                            checked={enforce2fa}
                            onChange={onCheckboxChange}
                            label={translation('ENFORCE_MFA')}
                        />
                    </div>
                </div>

                <div className='modal__body--row'>
                    <PasslyDivider />
                    <h4>{translation('POLICY_PROVISIONING_NOTIFY')}</h4>
                    <Typography variant='body1'>{translation('POLICY_PROVISIONING_NOTIFY_DESCRIPTION')}</Typography>
                    <div className='modal__body--inner_row'>
                        <PasslyCheckbox
                            name='onboardingEmail'
                            checked={onboardingEmail}
                            onChange={onCheckboxChange}
                            disabled={provisioningSelected !== provisioned}
                            label={translation('NOTIFY_USERS')}
                        />
                    </div>
                </div>

                <div className='modal__body--row'>
                    <PasslyDivider />
                    <h4>{translation('POLICY_PROVISIONING_REMAP')}</h4>
                    <Typography variant='body1'>{translation('POLICY_PROVISIONING_REMAP_DESCRIPTION')}</Typography>
                    <div className='modal__body--inner_row'>
                        <PasslyCheckbox
                            name='agentMerge'
                            checked={agentMerge}
                            onChange={onCheckboxChange}
                            label={translation('REMAP_USERS')}
                        />
                    </div>
                </div>
                {
                    errorMessage &&
                    <div className='modal__error'>{errorMessage}</div>
                }
            </form>
            <ModalActionsComponent
                primaryBtn={okButtonText()}
                secondaryBtn={translation('CANCEL')}
                onClickPrimary={handleSubmitBtn}
                onClickSecondary={handleCancel}
                id='addEditProvPolicy'
            >
            </ModalActionsComponent>
        </>
    );
}
